import useFormattedDate from "@/dates/use-formatted-date";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import type {
  ReturnRequestShippingInfoDto,
  ReturnRequestStatusDto,
} from "@babackapp/private-api-client";
import { cn } from "@babackapp/customer-ui/lib/utils";
import { resolveReturnMethodKind } from "../../utils/resolve-return-method";
import { resolveTimelineEntries } from "../../utils/return-status-timeline";

type Props = {
  status: ReturnRequestStatusDto;
  shippingInfo: ReturnRequestShippingInfoDto;
};

export function ReturnStatusTimeline({ status, shippingInfo }: Props) {
  const t = useCustomText();
  const entries = resolveTimelineEntries(status, resolveReturnMethodKind(shippingInfo));

  if (entries.length === 0) return null;

  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium text-foreground mb-2">
        {t("returnRequests.details.timeline.title")}
      </p>
      <div className="flex flex-col">
        {entries.map((entry, index) => (
          <TimelineItem
            key={entry.key}
            translationKey={entry.translationKey}
            doneAt={entry.doneAt}
            isLast={index === entries.length - 1}
            isCancelled={entry.key === "cancelled" || entry.key === "error"}
          />
        ))}
      </div>
    </div>
  );
}

type TimelineItemProps = {
  translationKey: string;
  doneAt: number;
  isLast: boolean;
  isCancelled: boolean;
};

function TimelineItem({ translationKey, doneAt, isLast, isCancelled }: TimelineItemProps) {
  const t = useCustomText();
  const { formattedDate } = useFormattedDate(doneAt);

  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div
          className={cn(
            "size-2.5 rounded-full mt-1 shrink-0",
            isCancelled ? "bg-destructive" : "bg-muted-foreground/40",
          )}
        />
        {!isLast && <div className="w-px flex-1 bg-border mt-1 mb-1" />}
      </div>
      <div className={cn("flex flex-col pb-4", isLast && "pb-0")}>
        <span className={cn("text-sm font-medium", isCancelled ? "text-destructive" : "text-muted-foreground")}>
          {t(translationKey)}
        </span>
        <span className="text-xs text-muted-foreground/60">{formattedDate}</span>
      </div>
    </div>
  );
}
